import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import store from '@/store';
import invitacionesService from '@/services/perfil/invitaciones';
import { toError } from '@/services/perfil/_normalize';

/**
 * useStepperZonas — Estado y operaciones del Paso 1 (selección de zona).
 *
 * Responsabilidades:
 *  - Carga de zonas disponibles para el evento/sesión.
 *  - Carga opcional del SVG del recinto (falla silenciosamente).
 *  - Selección / deselección de zona.
 *  - Guards: isZonasEmpty, puedeAvanzarPasoZona.
 *
 * @param {import('vue').Ref<Object|null>} evento       Ref del evento activo
 * @param {() => string|number}             getAbonadoId Función que retorna el ID del abonado
 */
export function useStepperZonas(evento, getAbonadoId) {

    // ─── State ────────────────────────────────────────────────────────────
    const zonas = ref([]);
    const loadingZonas = ref(false);
    const errorZonas = ref(null);

    /** Zona elegida por el usuario. Incluye el objeto completo de la zona. */
    const zonaSeleccionada = ref(null);

    /** SVG del mapa del recinto. Se obtiene via fetchSvgRecinto() usando place.id */
    const svgRecinto = ref('');

    // ─── Computed ─────────────────────────────────────────────────────────
    /**
     * `true` si las zonas se cargaron sin error pero el array está vacío.
     */
    const isZonasEmpty = computed(
        () => !loadingZonas.value && !errorZonas.value && zonas.value.length === 0
    );

    /**
     * Paso 1 → puede avanzar si hay zona seleccionada.
     */
    const puedeAvanzarPasoZona = computed(
        () => Boolean(zonaSeleccionada.value)
    );

    // ─── API calls ────────────────────────────────────────────────────────

    /**
     * Carga el listado de zonas para el evento/sesión activos.
     */
    const fetchZonas = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId || !evento.value) return;

        loadingZonas.value = true;
        errorZonas.value = null;
        try {
            const mostrarPrecioMasAlto = store.getters.getTeatro?.mostrar_precio_mas_alto_en_la_zona ?? false;
            const result = await invitacionesService.getZonas(
                abonadoId,
                evento.value.evento_id,
                evento.value.sesion_id,
                mostrarPrecioMasAlto,
            );
            zonas.value = result;
        } catch (err) {
            const normalized = toError(err);
            errorZonas.value = normalized.message ??
                t('perfil.invitaciones.stepper.error_zonas', {}, 'Error al cargar las zonas disponibles.');
            zonas.value = [];
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperZonas] Error al obtener zonas:', err);
            }
        } finally {
            loadingZonas.value = false;
        }
    };

    /**
     * Carga el SVG del mapa del recinto para el Paso 1.
     * Falla silenciosamente: si no hay place o el endpoint falla, la UI sigue
     * funcionando solo con la tabla de zonas.
     */
    const fetchSvgRecinto = async() => {
        const placeId = evento.value?.place?.id ?? evento.value?.place_id ?? null;
        if (!placeId) return;

        try {
            const svg = await invitacionesService.getSvgRecinto(placeId);
            svgRecinto.value = svg ?? '';
        } catch (err) {
            svgRecinto.value = '';
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperZonas] Error al obtener SVG del recinto:', err);
            }
        }
    };

    // ─── Selección ────────────────────────────────────────────────────────

    /**
     * Selecciona o deselecciona una zona.
     *
     * @param {Object} zona  Objeto zona del listado
     * @returns {boolean}  `true` si se seleccionó una zona nueva, `false` si se deseleccionó
     */
    const seleccionarZona = zona => {
        if (zonaSeleccionada.value && zonaSeleccionada.value.id === zona.id) {
            zonaSeleccionada.value = null;
            return false;
        }
        zonaSeleccionada.value = zona;
        return true;
    };

    /**
     * Limpia la zona seleccionada.
     */
    const limpiarZona = () => {
        zonaSeleccionada.value = null;
    };

    // ─── Reset ────────────────────────────────────────────────────────────
    const reset = () => {
        zonas.value = [];
        zonaSeleccionada.value = null;
        svgRecinto.value = '';
        errorZonas.value = null;
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        zonas,
        loadingZonas,
        errorZonas,
        zonaSeleccionada,
        svgRecinto,
        fetchZonas,
        fetchSvgRecinto,
        seleccionarZona,
        limpiarZona,
        isZonasEmpty,
        puedeAvanzarPasoZona,
        reset,
    };
}
